/* eslint-env browser */

/**
 * Infinite Scroll
 * Used on all pages where there is a list of posts (homepage, tag index, etc).
 *
 * When the page is scrolled to 300px from the bottom, the next page of posts
 * is fetched by following the the <link rel="next" href="..."> that is output
 * by {{ghost_head}}.
 *
 * The individual post items are extracted from the fetched pages by looking for
 * a wrapper element with the class "post-card". Any found elements are appended
 * to the element with the class "post-feed" in the currently viewed page.
 *
 * The loader is also published as `window.casperFeed` so other scripts can pull
 * pages in on demand: the homepage category filter needs the whole feed in the
 * DOM before it can filter it, otherwise a tag whose posts sit on page 2 would
 * look empty. The API is exposed even when scroll-driven loading is off
 * (html.no-infinite-scroll) - only the listeners below are conditional.
 */

(function (window, document) {
    // next link element
    var nextElement = document.querySelector('link[rel=next]');

    // post feed element
    var feedElement = document.querySelector('.post-feed');

    var buffer = 300;

    var ticking = false;
    var loading = false;
    // set once the feed runs out of pages - stops any further requests
    var exhausted = !nextElement || !feedElement;
    // in-flight request, so concurrent callers share one fetch per page
    var pending = null;
    var cardListeners = [];

    var lastScrollY = window.scrollY;
    var lastWindowHeight = window.innerHeight;
    var lastDocumentHeight = document.documentElement.scrollHeight;

    function stop() {
        exhausted = true;
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
    }

    // Fetches the next page and appends its cards, resolving with the cards
    // that were added - an empty array once there is nothing left to load.
    function loadNext() {
        if (pending) {
            return pending;
        }

        if (exhausted) {
            return Promise.resolve([]);
        }

        loading = true;

        pending = window.fetch(nextElement.href, {credentials: 'same-origin'})
            .then(function (response) {
                if (!response.ok) {
                    throw new Error('Feed page returned ' + response.status);
                }
                return response.text();
            })
            .then(function (html) {
                var page = new window.DOMParser().parseFromString(html, 'text/html');
                var cards = [];

                page.querySelectorAll('article.post-card').forEach(function (item) {
                    // document.importNode is important, without it the item's
                    // owner document will be different which can break resizing
                    // of `object-fit: cover` images in Safari
                    var card = document.importNode(item, true);
                    feedElement.appendChild(card);
                    cards.push(card);
                });

                // set next link
                var resNextElement = page.querySelector('link[rel=next]');
                if (resNextElement) {
                    nextElement.href = resNextElement.href;
                } else {
                    stop();
                }

                settle();

                cardListeners.forEach(function (listener) {
                    listener(cards);
                });

                return cards;
            })
            .catch(function () {
                // A 404 past the last page, or a dropped connection: either way
                // there is no point asking for more.
                stop();
                settle();
                return [];
            });

        return pending;
    }

    // Loads every remaining page, one after another.
    function loadAll() {
        if (exhausted) {
            return Promise.resolve();
        }

        return loadNext().then(loadAll);
    }

    function settle() {
        // sync status
        lastDocumentHeight = document.documentElement.scrollHeight;
        ticking = false;
        loading = false;
        pending = null;
    }

    function onUpdate() {
        // return if already loading, or out of pages
        if (loading || exhausted) {
            ticking = false;
            return;
        }

        // return if not scroll to the bottom
        if (lastScrollY + lastWindowHeight <= lastDocumentHeight - buffer) {
            ticking = false;
            return;
        }

        loadNext();
    }

    function requestTick() {
        ticking || window.requestAnimationFrame(onUpdate);
        ticking = true;
    }

    function onScroll() {
        lastScrollY = window.scrollY;
        requestTick();
    }

    function onResize() {
        lastWindowHeight = window.innerHeight;
        lastDocumentHeight = document.documentElement.scrollHeight;
        requestTick();
    }

    window.casperFeed = {
        hasMore: function () {
            return !exhausted;
        },
        loadNext: loadNext,
        loadAll: loadAll,
        // called with the cards appended by each loaded page
        onCards: function (listener) {
            cardListeners.push(listener);
        }
    };

    if (exhausted || document.documentElement.classList.contains('no-infinite-scroll')) {
        return;
    }

    window.addEventListener('scroll', onScroll, {passive: true});
    window.addEventListener('resize', onResize);

    requestTick();
})(window, document);
